import { useEffect, useRef } from "react";
import { motion, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import { PHI } from "@/lib/glass";
import { crossing } from "@/lib/crossing";

const passed = (p) => {
  const k = Math.min(Math.max((p - 0.35) / 0.45, 0), 1);
  return k * k * (3 - 2 * k);
};

// The border between the company above and the founder's why below. A bold
// band of gold light ignites from its centre as the visitor approaches, a
// lozenge turns at the crossing point, and a single thread of light descends
// into the manifesto — so the crossing is felt over the time it takes to scroll it.
export default function Threshold() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const lineScale = useTransform(scrollYProgress, [0.12, 0.5], [0, 1]);
  const haloOpacity = useTransform(scrollYProgress, [0.15, 0.5, 0.85], [0, 1, 0.35]);
  const markOpacity = useTransform(scrollYProgress, [0.3, 0.5], [0, 1]);
  const markScale = useTransform(scrollYProgress, [0.3, 0.5], [0.4, 1]);
  const threadScale = useTransform(scrollYProgress, [0.48, 0.78], [0, 1]);
  const aboveOpacity = useTransform(scrollYProgress, [0.2, 0.42, 0.62], [0, 1, 0.35]);
  const belowOpacity = useTransform(scrollYProgress, [0.46, 0.66], [0, 1]);

  // The room's light warms toward candle-gold as the visitor passes into the why.
  useMotionValueEvent(scrollYProgress, "change", (p) => {
    crossing.k = passed(p);
  });
  useEffect(() => {
    crossing.k = passed(scrollYProgress.get());
    return () => {
      crossing.k = 0;
    };
  }, [scrollYProgress]);

  const caption =
    "block text-center text-[10px] font-light uppercase tracking-[0.42em] md:text-[11px] md:tracking-[0.5em]";

  return (
    <section
      ref={ref}
      data-testid="footer-threshold"
      aria-label="The threshold between the company and the founder's reason"
      className="relative overflow-hidden px-6"
      style={{ paddingTop: PHI.s5, paddingBottom: PHI.s4 }}
    >
      <motion.span
        data-testid="threshold-caption-above"
        style={{ opacity: aboveOpacity, marginBottom: PHI.s3 }}
        className={`${caption} text-[#F5F5F7]/55`}
      >
        The work · Prism Pixel
      </motion.span>

      <div className="relative mx-auto flex h-10 max-w-[72rem] items-center justify-center">
        <motion.div
          aria-hidden="true"
          style={{ opacity: haloOpacity }}
          className="threshold-halo pointer-events-none absolute inset-x-[-10%] top-1/2 h-40 -translate-y-1/2"
        />
        <motion.div
          data-testid="threshold-line"
          aria-hidden="true"
          style={{ scaleX: lineScale }}
          className="threshold-line absolute inset-x-0 top-1/2 h-[2px] origin-center -translate-y-1/2"
        />
        <motion.div
          data-testid="threshold-mark"
          aria-hidden="true"
          style={{ opacity: markOpacity, scale: markScale }}
          className="threshold-mark relative z-10 flex h-10 w-10 items-center justify-center"
        >
          <span className="threshold-lozenge block h-3.5 w-3.5 rotate-45 border border-[#8E8E93]" />
          <span className="absolute h-1 w-1 rounded-full bg-[#D4AF37]" />
        </motion.div>
      </div>

      <motion.span
        data-testid="threshold-caption-below"
        style={{ opacity: belowOpacity, marginTop: PHI.s3 }}
        className={`${caption} text-[#84DFF4]/80`}
      >
        The why · Charles Buerger
      </motion.span>

      <motion.div
        data-testid="threshold-thread"
        aria-hidden="true"
        style={{ scaleY: threadScale, marginTop: PHI.s3 }}
        className="threshold-thread mx-auto h-24 w-px origin-top md:h-32"
      />
    </section>
  );
}
